import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import MultiSelectTicker from './MultiSelectTicker';
import AccordionFormSection from './Screener/AccordionFormSection';
import StrategyCard from './Screener/StrategyCard';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { formatTickersForAPI, basicStrategies } from '../utils/presetMappings';
import { cardToggleTemplate, actionButtonTemplate } from '../theme'; // Using centralized theme templates
import { Play } from 'lucide-react';

const ScreenerBasicForm = forwardRef(({
  onAddToQueue,
  onAddAndRun,
  onSaveScenario,
  queueCount,
  loading,
  hideButtons = false, // New prop to hide buttons when using FloatingActionPanel
  onFormValidityChange // Callback to notify parent when form validity changes
}, ref) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Form values
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [direction, setDirection] = useState(null); // 'call' or 'put'
  const [selectedStrategies, setSelectedStrategies] = useState([]); // Array of strategy keys for multi-select
  const [timeframe, setTimeframe] = useState(60); // 60, 135, 240, or 365 days
  const [strikePosition, setStrikePosition] = useState(0); // -50 to +100
  const [stockMovement, setStockMovement] = useState(10); // -50 to +200

  // Accordion state
  const [expandedSections, setExpandedSections] = useState(
    isMobile ? ['tickers'] : ['tickers', 'direction', 'strategy']
  );
  const [completedSections, setCompletedSections] = useState(new Set());
  const [activeSection, setActiveSection] = useState('tickers'); // Current active section

  // Helper function to find matching strategy based on scenario parameters
  const findMatchingStrategy = (scenario) => {
    // For puts, we need to reverse the adjustments that were made when creating the scenario
    const isPut = scenario.option_type === 'put';

    // Normalize the stock_gain_pct (always compare as positive)
    const normalizedStockGainPct = Math.abs(scenario.stock_gain_pct);

    // For puts, the strike_pct was flipped, so flip it back
    const normalizedStrikePct = isPut && scenario.strike_pct !== 0
      ? -scenario.strike_pct
      : scenario.strike_pct;

    // Find strategy that matches the parameters
    for (const [key, strategy] of Object.entries(basicStrategies)) {
      const matchesExpiration = Math.abs(strategy.days_until_exp - scenario.days_until_exp) <= 5;
      const matchesStrike = Math.abs(strategy.strike_pct - normalizedStrikePct) < 0.01;
      const matchesDaysToGain = Math.abs(strategy.days_to_gain - scenario.days_to_gain) <= 2;
      const matchesStockGain = Math.abs(strategy.stock_gain_pct - normalizedStockGainPct) < 0.01;

      if (matchesExpiration && matchesStrike && matchesDaysToGain && matchesStockGain) {
        return key;
      }
    }

    // If no exact match, try to find closest match based on expiration and strike
    let closestMatch = null;
    let closestDistance = Infinity;

    for (const [key, strategy] of Object.entries(basicStrategies)) {
      const expirationDiff = Math.abs(strategy.days_until_exp - scenario.days_until_exp);
      const strikeDiff = Math.abs(strategy.strike_pct - normalizedStrikePct);

      // Weight expiration difference less than strike difference
      const distance = (expirationDiff / 30) + (strikeDiff * 10);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestMatch = key;
      }
    }

    return closestMatch;
  };

  // Expose form state and methods to parent component
  useImperativeHandle(ref, () => ({
    getFormState: () => ({
      selectedTickers,
      direction,
      selectedStrategies,
      isValid: isFormValid()
    }),
    buildScenarios,
    isFormValid,
    resetForm: () => {
      setSelectedTickers([]);
      setDirection(null);
      setSelectedStrategies([]);
      setCompletedSections(new Set());
      setActiveSection('tickers');
      setExpandedSections(isMobile ? ['tickers'] : ['tickers', 'direction', 'strategy']);
    },
    // Guided-tour demo population. Applies `value` to `part` ONLY when that
    // part is currently empty — never overwrites the user's own selections.
    tourPopulate: (part, value) => {
      if (part === 'tickers' && selectedTickers.length === 0 && Array.isArray(value)) {
        setSelectedTickers(value.map(t => ({ value: t, label: t })));
        setCompletedSections(prev => new Set(prev).add('tickers'));
      } else if (part === 'direction' && !direction && value) {
        setDirection(value);
        setCompletedSections(prev => new Set(prev).add('direction'));
      } else if (part === 'strategy' && selectedStrategies.length === 0 && Array.isArray(value)) {
        setSelectedStrategies(value.filter(k => basicStrategies[k]));
        setCompletedSections(prev => new Set(prev).add('strategy'));
      }
    },
    loadScenario: (scenario) => {
      console.log('Loading scenario into form:', scenario);

      // Set tickers
      const tickerArray = Array.isArray(scenario.tickers)
        ? scenario.tickers
        : scenario.tickers.split(',').map(t => t.trim());

      const tickerObjects = tickerArray.map(ticker => ({
        value: ticker,
        label: ticker
      }));
      setSelectedTickers(tickerObjects);

      // Set direction
      setDirection(scenario.option_type);

      // Match parameters to strategy
      const matchedStrategy = findMatchingStrategy(scenario);
      if (matchedStrategy) {
        setSelectedStrategies([matchedStrategy]);
        console.log('Matched to strategy:', matchedStrategy);
      } else {
        // Clear strategy selection if no match
        setSelectedStrategies([]);
        console.log('No matching strategy found for parameters');
      }

      // Mark sections as completed
      setCompletedSections(new Set(['tickers', 'direction', 'strategy']));

      // Expand all sections on desktop, or just the strategy section on mobile
      if (isMobile) {
        setExpandedSections(['strategy']);
        setActiveSection('strategy');
      } else {
        setExpandedSections(['tickers', 'direction', 'strategy']);
      }
    }
  }));

  // Unified function to handle section transitions
  const activateNextSection = (currentSection, nextSection = null) => {
    if (!isMobile) return;

    // Mark current section as completed
    setCompletedSections(prev => new Set(prev).add(currentSection));

    if (nextSection) {
      // Moving to next section: collapse current, expand next
      setExpandedSections([nextSection]);
      setActiveSection(nextSection);
    } else {
      // Form complete: collapse all sections
      setExpandedSections([]);
      setActiveSection('complete');
    }
  };

  // Auto-advance logic (mobile only)
  useEffect(() => {
    if (!isMobile) return;
    if (selectedTickers.length > 0) {
      activateNextSection('tickers', 'direction');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTickers, isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    if (direction) {
      activateNextSection('direction', 'strategy');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction, isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    if (selectedStrategies.length > 0) {
      // Mark strategy as completed and collapse it after a brief delay
      setCompletedSections(prev => new Set(prev).add('strategy'));
      // Auto-collapse after selection to show floating panel
      setTimeout(() => {
        setExpandedSections([]);
        setActiveSection('complete');
      }, 300); // Brief delay so user sees the checkmark appear
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStrategies, isMobile]);

  // Monitor form validity and notify parent when it changes
  useEffect(() => {
    if (onFormValidityChange) {
      const valid = isFormValid();
      onFormValidityChange(valid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTickers, direction, selectedStrategies, onFormValidityChange]);

  // Helper: Determine if section should be disabled (mobile only)
  const isSectionDisabled = (section) => {
    if (!isMobile) return false; // Desktop: all sections enabled

    // If section is already completed, it should be accessible for editing
    if (completedSections.has(section)) {
      return false; // Allow reopening completed sections
    }

    // Section order - simplified to just 3 sections now
    const sectionOrder = ['tickers', 'direction', 'strategy'];
    const sectionIndex = sectionOrder.indexOf(section);

    // Find the highest completed section index
    let highestCompletedIndex = -1;
    sectionOrder.forEach((s, idx) => {
      if (completedSections.has(s)) {
        highestCompletedIndex = Math.max(highestCompletedIndex, idx);
      }
    });

    // Disable only if this section comes after the highest completed section
    // This prevents skipping ahead but allows going back
    return sectionIndex > highestCompletedIndex + 1;
  };

  // Handle section toggle (works on both mobile and desktop)
  const handleToggle = (section) => {
    if (isMobile) {
      // On mobile, allow reopening completed sections or advancing to the next section
      // Toggle between expanded and collapsed for the clicked section
      setExpandedSections(prev => {
        if (prev.includes(section)) {
          // If section is already open, close it
          return [];
        } else {
          // Open only this section (mobile shows one at a time)
          return [section];
        }
      });

      // Update active section if it's a valid progression
      const sectionOrder = ['tickers', 'direction', 'strategy'];
      const sectionIndex = sectionOrder.indexOf(section);
      if (sectionIndex >= 0) {
        setActiveSection(section);
      }
    } else {
      // Desktop: allow multiple sections open
      setExpandedSections(prev =>
        prev.includes(section)
          ? prev.filter(s => s !== section)
          : [...prev, section]
      );
    }
  };

  // Check if form is valid
  const isFormValid = () => {
    return (
      selectedTickers.length > 0 &&
      direction !== null &&
      selectedStrategies.length > 0
    );
  };

  // Generate tooltip message for disabled button
  const getDisabledTooltipMessage = () => {
    const missingItems = [];

    if (selectedTickers.length === 0) {
      missingItems.push('Tickers');
    }
    if (direction === null) {
      missingItems.push('Market Direction');
    }
    if (selectedStrategies.length === 0) {
      missingItems.push('Strategy');
    }

    if (missingItems.length === 0) {
      return '';
    }

    if (missingItems.length === 1) {
      return `Please select ${missingItems[0]}`;
    } else if (missingItems.length === 2) {
      return `Please select ${missingItems[0]} and ${missingItems[1]}`;
    } else {
      return `Please complete all sections`;
    }
  };

  // Build scenarios for API (multiple strategies)
  const buildScenarios = () => {
    console.log(`📦 buildScenarios() called. Selected strategies:`, selectedStrategies);
    if (!isFormValid()) {
      console.log('⚠️ Form is not valid, returning empty array');
      return [];
    }

    const tickerValues = formatTickersForAPI(selectedTickers);

    // Map each selected strategy to a scenario
    const scenarios = selectedStrategies.map(strategyKey => {
      const strategy = basicStrategies[strategyKey];

      // For puts, flip the stock gain percentage to negative and adjust strike interpretation
      const isPut = direction === 'put';
      const adjustedStockGainPct = isPut ? -Math.abs(strategy.stock_gain_pct) : Math.abs(strategy.stock_gain_pct);

      // For puts, strike interpretation reverses:
      // -25% strike (below current) = OTM for puts
      // +25% strike (above current) = ITM for puts
      // We need to flip the strike_pct sign for puts to maintain the ITM/OTM relationship
      const adjustedStrikePct = isPut && strategy.strike_pct !== 0 ? -strategy.strike_pct : strategy.strike_pct;

      // Adjust label: Bull → Bear for puts
      const adjustedLabel = isPut ? strategy.label.replace('Bull', 'Bear') : strategy.label;

      return {
        tickers: tickerValues,
        option_type: direction,
        days_until_exp: strategy.days_until_exp,
        strike_pct: adjustedStrikePct,
        days_to_gain: strategy.days_to_gain,
        stock_gain_pct: adjustedStockGainPct,
        allocation: strategy.allocation,
        _exitPercent: strategy.days_to_gain / strategy.days_until_exp,  // Original exit % for display
        label: `${tickerValues.join(',')} ${direction === 'call' ? 'Calls' : 'Puts'} - ${adjustedLabel}`
      };
    });

    console.log(`📊 buildScenarios() built ${scenarios.length} scenario(s):`, scenarios.map(s => s.label));
    return scenarios;
  };

  // Handle submit (add all selected strategies to queue)
  const handleSubmit = () => {
    const scenarios = buildScenarios();
    if (scenarios.length > 0 && onAddToQueue) {
      scenarios.forEach(scenario => onAddToQueue(scenario));
    }
  };

  // Run immediately — append the form's scenarios to the queue AND fire the
  // screener in one tap.
  const handleRunNow = () => {
    const scenarios = buildScenarios();
    if (scenarios.length > 0 && onAddAndRun) {
      onAddAndRun(scenarios);
    }
  };

  // Handle direction change
  const handleDirectionChange = (newDirection) => {
    setDirection(newDirection);
    if (!completedSections.has('direction')) {
      setCompletedSections(prev => new Set(prev).add('direction'));
    }
  };

  // Handle ticker change
  const handleTickerChange = (tickers) => {
    setSelectedTickers(tickers || []);
    if (tickers && tickers.length > 0 && !completedSections.has('tickers')) {
      setCompletedSections(prev => new Set(prev).add('tickers'));
    }
  };

  // Handle timeframe change
  const handleTimeframeChange = (days) => {
    setTimeframe(days);
    if (!completedSections.has('expiration')) {
      setCompletedSections(prev => new Set(prev).add('expiration'));
    }
  };

  // Handle strike change
  const handleStrikeChange = (value) => {
    setStrikePosition(value);
    if (!completedSections.has('strike')) {
      setCompletedSections(prev => new Set(prev).add('strike'));
    }
  };

  // Handle movement change
  const handleMovementChange = (value) => {
    setStockMovement(value);
    if (!completedSections.has('movement')) {
      setCompletedSections(prev => new Set(prev).add('movement'));
    }
  };

  // Handle strategy selection (toggle behavior for multi-select)
  const handleStrategyChange = (strategyKey) => {
    setSelectedStrategies(prev => {
      let newSelection;
      if (prev.includes(strategyKey)) {
        // Already selected - remove it
        newSelection = prev.filter(k => k !== strategyKey);
        console.log(`🔴 Deselected ${strategyKey}. Selected strategies:`, newSelection);
      } else {
        // Not selected - add it
        newSelection = [...prev, strategyKey];
        console.log(`🟢 Selected ${strategyKey}. Selected strategies:`, newSelection);
      }
      return newSelection;
    });

    // Mark section as completed when at least one strategy is selected
    if (!completedSections.has('strategy') && selectedStrategies.length >= 0) {
      setCompletedSections(prev => new Set(prev).add('strategy'));
    }
  };

  // Get strategy label for summary
  const getStrategyLabel = () => {
    if (selectedStrategies.length === 0) return '';
    if (selectedStrategies.length === 1) {
      const strategy = basicStrategies[selectedStrategies[0]];
      return strategy ? strategy.label : '';
    }
    return `${selectedStrategies.length} strategies selected`;
  };

  // Adjust strategy title based on direction (Bull → Bear for puts)
  const getAdjustedTitle = (label, isPut) => {
    if (isPut) {
      return label.replace('Bull', 'Bear');
    }
    return label;
  };

  return (
    <>
      <div className="pb-0">
        {/* Section 1: Tickers */}
        <AccordionFormSection
          title="Select Multiple Tickers"
          icon=""
          summary={
            selectedTickers.length > 0
              ? selectedTickers.map(t => t.value).join(', ')
              : ''
          }
          isExpanded={expandedSections.includes('tickers')}
          isDisabled={isSectionDisabled('tickers')}
          isCompleted={completedSections.has('tickers')}
          onToggle={() => handleToggle('tickers')}
          isMobile={isMobile}
          dataTour="tickers-section"
        >
          <MultiSelectTicker
            value={selectedTickers}
            onChange={handleTickerChange}
            placeholder="Search and select tickers (e.g., AAPL, MSFT, GOOGL)..."
            controlBackground="#1f2937"
          />
        </AccordionFormSection>

        {/* Section 2: Direction */}
        <AccordionFormSection
          title="Market Direction"
          icon=""
          summary={
            direction
              ? direction === 'call'
                ? 'Bullish (Calls)'
                : 'Bearish (Puts)'
              : ''
          }
          isExpanded={expandedSections.includes('direction')}
          isDisabled={isSectionDisabled('direction')}
          isCompleted={completedSections.has('direction')}
          onToggle={() => handleToggle('direction')}
          isMobile={isMobile}
          dataTour="direction-section"
        >
          <div className={`direction-grid ${isMobile ? 'grid-cols-2' : 'grid-cols-2'}`}>
            <button
              type="button"
              onClick={() => handleDirectionChange('call')}
              className={`${cardToggleTemplate.cssClasses.directionBase} ${direction === 'call' ? cardToggleTemplate.cssClasses.selected : ''}`}
            >
              <span className={cardToggleTemplate.cssClasses.directionTitle}>Bullish</span>
              <span className={cardToggleTemplate.cssClasses.directionSubtitle}>Buy Calls</span>
            </button>
            <button
              type="button"
              onClick={() => handleDirectionChange('put')}
              className={`${cardToggleTemplate.cssClasses.directionBase} ${direction === 'put' ? cardToggleTemplate.cssClasses.selected : ''}`}
            >
              <span className={cardToggleTemplate.cssClasses.directionTitle}>Bearish</span>
              <span className={cardToggleTemplate.cssClasses.directionSubtitle}>Buy Puts</span>
            </button>
          </div>
        </AccordionFormSection>

        {/* Section 3: Strategy */}
        <AccordionFormSection
          title="Strategy"
          icon=""
          summary={getStrategyLabel()}
          isExpanded={expandedSections.includes('strategy')}
          isDisabled={isSectionDisabled('strategy')}
          isCompleted={completedSections.has('strategy')}
          onToggle={() => handleToggle('strategy')}
          isMobile={isMobile}
          dataTour="strategy-section"
        >
          <div className="strategy-card-grid">
            {/* Organize strategies by expiration: 3 rows x 2 columns */}
            {[
              // Row 1: 1-3 months
              ['short-itm-call', 'short-otm-call'],
              // Row 2: 6-11 months
              ['medium-itm-call', 'medium-otm-call'],
              // Row 3: 12+ months (LEAPS)
              ['long-itm-call', 'long-otm-call']
            ].flat().map((strategyKey) => {
              const strategy = basicStrategies[strategyKey];
              const isPut = direction === 'put';

              // Adjust the displayed expected move based on direction
              const displayedExpectedMove = isPut ? '-20%' : '+20%';

              // Adjust strike label for puts (ITM/OTM character preserved, physical position flips)
              let displayedStrike = strategy.display.strike;
              if (isPut && strategy.strike_pct !== 0) {
                // ITM call → ITM put: strike flips from below current to above current
                // OTM call → OTM put: strike flips from above current to below current
                if (strategy.strike_pct < 0) {
                  displayedStrike = 'ITM +25%';  // ITM call (-25%) → ITM put (+25% above current)
                } else {
                  displayedStrike = 'OTM -25%';  // OTM call (+25%) → OTM put (-25% below current)
                }
              }

              // Adjust tooltip for puts: replace call-specific language
              let displayedTooltip = strategy.tooltip;
              if (isPut) {
                displayedTooltip = displayedTooltip
                  .replace(/\bcall\b/gi, 'put')
                  .replace(/\bupside\b/gi, 'downside')
                  .replace(/\bgrowth\b/gi, 'decline');
              }

              return (
                <StrategyCard
                  key={strategyKey}
                  strategyKey={strategyKey}
                  title={getAdjustedTitle(strategy.label, isPut)}
                  subtitle={displayedTooltip}
                  expiration={strategy.display.expiration}
                  strike={displayedStrike}
                  daysToTarget={strategy.display.daysToTarget}
                  expectedMove={displayedExpectedMove}
                  optionType={direction}
                  isSelected={selectedStrategies.includes(strategyKey)}
                  onClick={handleStrategyChange}
                />
              );
            })}
          </div>
        </AccordionFormSection>

        {/* Section 4: Expiration - Hidden, set by strategy */}
        {/* Commenting out for now since strategy sets this
        <AccordionFormSection
          title="Expiration Timeframe"
          icon=""
          summary={timeframe ? `${timeframe} days` : ''}
          isExpanded={expandedSections.includes('expiration')}
          isDisabled={isSectionDisabled('expiration')}
          isCompleted={completedSections.has('expiration')}
          onToggle={() => handleToggle('expiration')}
          isMobile={isMobile}
        >
          <ExpirationTimeframeButtons
            value={timeframe}
            onChange={handleTimeframeChange}
            isMobile={isMobile}
          />
        </AccordionFormSection>
        */}

        {/* Section 5: Strike Position - Hidden, set by strategy */}
        {/* Commenting out for now since strategy sets this
        <AccordionFormSection
          title="Strike Position"
          icon=""
          summary={
            strikePosition !== null
              ? `${strikePosition > 0 ? '+' : ''}${strikePosition}%`
              : ''
          }
          isExpanded={expandedSections.includes('strike')}
          isDisabled={isSectionDisabled('strike')}
          isCompleted={completedSections.has('strike')}
          onToggle={() => handleToggle('strike')}
          isMobile={isMobile}
        >
          <StrikePositionSlider
            value={strikePosition}
            onChange={handleStrikeChange}
            optionType={direction}
            isMobile={isMobile}
          />
        </AccordionFormSection>
        */}

        {/* Section 6: Stock Movement - Hidden, set by strategy */}
        {/* Commenting out for now since strategy sets this
        <AccordionFormSection
          title="Expected Stock Movement"
          icon=""
          summary={
            stockMovement !== null
              ? `${stockMovement > 0 ? '+' : ''}${stockMovement}%`
              : ''
          }
          isExpanded={expandedSections.includes('movement')}
          isDisabled={isSectionDisabled('movement')}
          isCompleted={completedSections.has('movement')}
          onToggle={() => handleToggle('movement')}
          isMobile={isMobile}
        >
          <StockMovementSlider
            value={stockMovement}
            onChange={handleMovementChange}
            isMobile={isMobile}
          />
        </AccordionFormSection>
        */}
      </div>

      {/* Action Buttons — Run / Add to Queue / Save */}
      {!hideButtons && (
        <div className={isMobile ? 'fixed-bottom-button' : 'inline-button'} data-tour="form-actions">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleRunNow}
              disabled={!isFormValid() || loading}
              className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.runSolid} flex-1 ${!isFormValid() ? actionButtonTemplate.cssClasses.disabled : ''}`}
              style={{ position: 'relative' }}
              title="Run these strategies immediately"
            >
              {loading ? (
                <span className="spinner"></span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Play size={14} style={{ fill: 'currentColor' }} />
                  {selectedStrategies.length > 1
                    ? `Run ${selectedStrategies.length} Strategies`
                    : 'Run Strategy'}
                </span>
              )}
              {!isFormValid() && (
                <span className={actionButtonTemplate.cssClasses.tooltip}>
                  {getDisabledTooltipMessage()}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!isFormValid() || loading}
              className={`${actionButtonTemplate.cssClasses.base} flex-1 ${!isFormValid() ? actionButtonTemplate.cssClasses.disabled : ''}`}
              title="Add these strategies to the queue without running"
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span>
                  {selectedStrategies.length > 1
                    ? `+ Add ${selectedStrategies.length} to Queue`
                    : '+ Add to Queue'}
                </span>
                {queueCount > 0 && (
                  <span className="queue-badge-inline">{queueCount}</span>
                )}
              </span>
            </button>

            <button
              type="button"
              onClick={async () => {
                const scenarios = buildScenarios();
                if (scenarios.length > 0 && onSaveScenario) {
                  for (const scenario of scenarios) {
                    const success = await onSaveScenario(scenario);
                    if (!success) break;
                  }
                }
              }}
              disabled={!isFormValid() || loading}
              className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.success}`}
              title="Save this configuration for later use"
            >
              Save as preset
            </button>
          </div>
        </div>
      )}
    </>
  );
});

// Export helper functions for parent components to use
export const getFormValidation = (tickers, direction, strategies) => {
  return (
    tickers.length > 0 &&
    direction !== null &&
    strategies.length > 0
  );
};

export const buildScenariosFromState = (tickers, direction, strategies) => {
  if (!getFormValidation(tickers, direction, strategies)) return [];

  const tickerValues = formatTickersForAPI(tickers);

  return strategies.map(strategyKey => {
    const strategy = basicStrategies[strategyKey];
    const isPut = direction === 'put';
    const adjustedStockGainPct = isPut ? -Math.abs(strategy.stock_gain_pct) : Math.abs(strategy.stock_gain_pct);
    const adjustedStrikePct = isPut && strategy.strike_pct !== 0 ? -strategy.strike_pct : strategy.strike_pct;

    return {
      tickers: tickerValues,
      option_type: direction,
      days_until_exp: strategy.days_until_exp,
      strike_pct: adjustedStrikePct,
      days_to_gain: strategy.days_to_gain,
      stock_gain_pct: adjustedStockGainPct,
      allocation: strategy.allocation,
      _exitPercent: strategy.days_to_gain / strategy.days_until_exp,
      label: `${tickerValues.join(',')} ${direction === 'call' ? 'Calls' : 'Puts'} - ${strategy.label}`
    };
  });
};

export default ScreenerBasicForm;
